import React, { FunctionComponent } from 'react'
import { FleetPerformanceSectionIcon, NoData, OfficePrinterIcon } from '../Icons/Icons'
import shell from './BriefShell.module.css'
import styles from './BriefPreview.module.css'

/*
 * What a brief will look like, before there's data for it: the report's sections and charts with every
 * value swapped for a skeleton, blurred, under a single "No data available" message.
 */

const Bar: FunctionComponent<{ w: string; tall?: boolean }> = ({ w, tall }) => (
  <span className={`${styles.bar} ${tall ? styles.tall : ''} ${styles[`w${w}`]}`} />
)

const SkeletonTile: FunctionComponent = () => (
  <div className={`surface ${shell.metricSurface} ${styles.tile}`}>
    <Bar w='50' tall />
    <Bar w='80' />
  </div>
)

const SectionHeader: FunctionComponent<{ title: string; icon: React.ReactNode }> = ({ title, icon }) => (
  <header className={shell.sectionHeader}>
    <figure className='angular'>
      <i className='icon thirtytwo flex-noshrink'>{icon}</i>
    </figure>
    <h4 className={shell.sectionTitle}>{title}</h4>
  </header>
)

// bar heights for the skeleton print volume chart, so it reads as a chart without carrying any data
const BARS = [62, 70, 58, 74, 66, 60, 72, 68, 64, 76, 70, 66, 58, 54, 50]

export const BriefPreview: FunctionComponent<{ testId?: string }> = ({ testId }) => (
  <div className={styles.preview} data-testid={testId}>
    <div className={styles.report} aria-hidden='true'>
      <section className={shell.sectionWrapper}>
        <SectionHeader title='Fleet makeup' icon={<OfficePrinterIcon />} />
        <div className={styles.makeupRow}>
          {[0, 1].map(k => (
            <div key={k} className={styles.donutBlock}>
              <span className={styles.donut} />
              <div className={styles.legend}>
                <Bar w='70' />
                <Bar w='55' />
                <Bar w='65' />
                <Bar w='40' />
              </div>
            </div>
          ))}
          <div className={styles.tileStack}>
            <SkeletonTile />
            <SkeletonTile />
          </div>
        </div>
      </section>

      <section className={shell.sectionWrapper}>
        <SectionHeader title='Usage and performance' icon={<FleetPerformanceSectionIcon />} />
        <h4 className={styles.chartTitle}>Print volume</h4>
        <div className={styles.chart}>
          {BARS.map((h, i) => (
            <span key={i} className={`${styles.chartBar} ${styles[`h${h}`]}`} />
          ))}
        </div>
      </section>

      <section className={shell.sectionWrapper}>
        <SectionHeader title='Devices with embedded apps' icon={<OfficePrinterIcon />} />
        <div className={styles.tileRow}>
          <SkeletonTile />
          <SkeletonTile />
          <SkeletonTile />
        </div>
        <table className={`${shell.table} ${styles.table}`}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Model</th>
              <th>Total pages</th>
              <th>Color pages</th>
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2, 3].map(r => (
              <tr key={r}>
                <td>
                  <Bar w='70' />
                </td>
                <td>
                  <Bar w='55' />
                </td>
                <td>
                  <Bar w='40' />
                </td>
                <td>
                  <Bar w='35' />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>

    {/* one message for the whole brief, as on an empty chart */}
    <div className={styles.overlay} role='status'>
      <i className='icon twentyfour dip'>
        <NoData />
      </i>
      <h5>No data available</h5>
      <p>We're waiting on activity or still processing.</p>
    </div>
  </div>
)
